import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonBase, CardContent, CircularProgress, IconButton, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppContext } from '../../context/AppContext';
import { financialService, wealthService, apiErrorMessage } from '../../utils/api';
import { applyFetchedTotals, buildInvestmentPayload } from './investmentFields';
import { formatFetchedAt, formatINR, formatPct } from './format';
import ConnectDetailsDialog from './ConnectDetailsDialog';
import MfcCasModal from './MfcCasModal';
import AaConsentModal from './AaConsentModal';
import HoldingsList from './HoldingsList';

const ICONS = {
  MUTUAL_FUND: <BarChartIcon />,
  EQUITY: <ShowChartIcon />,
  NPS: <SavingsOutlinedIcon />,
  BANK: <AccountBalanceOutlinedIcon />,
};

const DIVIDER = '1px dashed #d0d0d0';
const blackButton = { bgcolor: '#000', color: '#fff', minWidth: 104, flexShrink: 0, '&:hover': { bgcolor: '#222' } };

const POLL_MS = 4000;
const POLL_LIMIT_MS = 5 * 60 * 1000;
const IN_FLIGHT = new Set(['CONSENT_PENDING', 'FETCHING']);

function CategoryRow({ category, onOpen, fetchedAt, refreshable, busy, onRefresh, connectionLabel }) {
  return (
    <Box sx={{ py: 2, borderBottom: DIVIDER }}>
      <ButtonBase
        onClick={onOpen}
        aria-label={`${category.label}: ${formatINR(category.value)}, ${formatPct(category.allocation_pct)} allocation. View holdings`}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left' }}
      >
        <Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0 }}>{ICONS[category.key]}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">{category.label}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
            {formatPct(category.allocation_pct)} ALLOCATION
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatINR(category.value)}</Typography>
        <ChevronRightIcon aria-hidden="true" sx={{ flexShrink: 0 }} />
      </ButtonBase>
      {fetchedAt && (
        // Outside the ButtonBase so the refresh icon is not a button inside a button.
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 5, minHeight: 28 }}>
          <Typography variant="caption" color="text.secondary" role={busy ? 'status' : undefined}>
            {busy ? 'Refreshing…' : `Updated ${formatFetchedAt(fetchedAt)}`}
          </Typography>
          {refreshable && (busy
            ? <CircularProgress size={14} aria-hidden="true" sx={{ ml: 0.5 }} />
            : (
              <IconButton size="small" onClick={onRefresh} aria-label={`Refresh ${connectionLabel}`} sx={{ p: 0.5 }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ))}
        </Box>
      )}
    </Box>
  );
}

function ConnectRow({ connection, onConnect, stalled, onCheckAgain }) {
  const { available, status, message } = connection;
  const pending = status === 'PENDING' && !stalled;
  const label = status === 'RECONNECT' ? 'Reconnect' : status === 'FAILED' ? 'Try again' : 'Connect';
  const caption = !available ? 'Coming soon' : stalled ? 'Taking longer than usual.' : message;
  const captionColor = status === 'FAILED' || status === 'RECONNECT' ? 'error.main' : 'text.secondary';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, borderBottom: DIVIDER }}>
      <Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0 }}>{ICONS[connection.categories[0].key]}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1">{connection.label}</Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: captionColor }} role={pending ? 'status' : undefined}>
            {caption}
          </Typography>
        )}
      </Box>
      {pending && (
        <CircularProgress size={24} aria-label={`${connection.label} is being fetched`} sx={{ mr: 3 }} />
      )}
      {stalled && (
        <Button variant="outlined" color="inherit" onClick={onCheckAgain} sx={{ minWidth: 104, flexShrink: 0 }}>
          Check again
        </Button>
      )}
      {!pending && !stalled && (
        <Button
          variant="contained"
          disabled={!available}
          onClick={onConnect}
          aria-label={`${label} ${connection.label}`}
          sx={blackButton}
        >
          {label}
        </Button>
      )}
    </Box>
  );
}

function ConnectedRows({ connection, onOpen, onRefresh, refreshing }) {
  // Show categories that returned holdings; if none did, show them all at ₹0.
  const withData = connection.categories.filter((c) => c.count > 0);
  const rows = withData.length ? withData : connection.categories;
  const busy = refreshing || Boolean(connection.pending_handle);
  return rows.map((cat) => (
    <CategoryRow
      key={cat.key}
      category={cat}
      onOpen={() => onOpen(cat.key)}
      fetchedAt={connection.as_of}
      refreshable={connection.refreshable}
      busy={busy}
      onRefresh={onRefresh}
      connectionLabel={connection.label}
    />
  ));
}

function WealthConnect() {
  const { state, actions } = useAppContext();
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [detailsFor, setDetailsFor] = useState(null); // connection whose mobile/PAN dialog is open
  const [mfcSession, setMfcSession] = useState(null);
  const [aaSession, setAaSession] = useState(null); // { title, redirectUrl, handle }
  const [stalled, setStalled] = useState({}); // consent handle -> true once polling gave up
  const [openCategory, setOpenCategory] = useState(null);
  const [refreshing, setRefreshing] = useState(null); // connection key while a refresh call is running
  const pollStartedRef = useRef({}); // consent handle -> first poll time
  const fetchedRef = useRef({}); // latest fetched totals, in investments field names
  const latestStateRef = useRef(state);
  const leavingToManualRef = useRef(false);
  latestStateRef.current = state;

  // Fetched holdings mapped to investments fields, copied into the form state so the
  // manual cards open pre-filled and the plan uses them.
  const syncTotals = useCallback(async () => {
    try {
      const { fields } = await wealthService.getInvestmentTotals();
      fetchedRef.current = fields || {};
      applyFetchedTotals(fetchedRef.current, actions.updateFormData);
    } catch {
      // non-blocking: the card still works without pre-fill
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSummary = useCallback(() => {
    setLoadError('');
    return wealthService.getSummary()
      .then((data) => { setSummary(data); return syncTotals(); })
      .catch((err) => setLoadError(apiErrorMessage(err, 'Could not load your wealth summary.')));
  }, [syncTotals]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Fetch journey skips the manual cards (where Others.js saves), so save here on leaving.
  useEffect(() => () => {
    const fields = fetchedRef.current;
    if (leavingToManualRef.current || !Object.keys(fields).length) return;
    const s = latestStateRef.current;
    financialService.submitInvestment(s.phoneNumber, buildInvestmentPayload(s, fields))
      .catch((err) => console.error('submitInvestment failed:', err));
  }, []);

  const switchToManual = async () => {
    await syncTotals(); // make sure the manual cards open with the latest fetched numbers
    leavingToManualRef.current = true; // Others.js saves at the end of the manual cards
    actions.updateFormData('investmentJourney', 'manual');
  };

  // Poll in-flight AA consents; the backend re-checks Finsense if a webhook is late.
  const pendingHandles = (summary?.connections || []).map((c) => c.pending_handle).filter(Boolean);
  const pendingKey = pendingHandles.join(',');
  const aaHandle = aaSession?.handle;
  useEffect(() => {
    if (!pendingKey) return undefined;
    const handles = pendingKey.split(',');
    const timer = setInterval(async () => {
      const now = Date.now();
      let changed = false;
      for (const handle of handles) {
        pollStartedRef.current[handle] = pollStartedRef.current[handle] || now;
        if (now - pollStartedRef.current[handle] > POLL_LIMIT_MS) {
          setStalled((prev) => (prev[handle] ? prev : { ...prev, [handle]: true }));
          continue;
        }
        try {
          const res = await wealthService.getAaStatus(handle);
          // Consent approved (or declined): close the Finvu window, keep polling the fetch.
          if (handle === aaHandle && res.status !== 'CONSENT_PENDING') setAaSession(null);
          if (!IN_FLIGHT.has(res.status)) changed = true;
        } catch {
          // transient; try again next tick
        }
      }
      if (changed) {
        setAaSession(null);
        loadSummary();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [pendingKey, aaHandle, loadSummary]);

  const checkAgain = (handle) => {
    pollStartedRef.current[handle] = Date.now();
    setStalled((prev) => { const next = { ...prev }; delete next[handle]; return next; });
    loadSummary();
  };

  const startConnect = (connection) => {
    setActionError('');
    setDetailsFor(connection);
  };

  // Called by the details dialog; throws so the dialog can show the error.
  const submitDetails = async ({ mobile, pan }) => {
    const connection = detailsFor;
    if (connection.key === 'MFC') {
      const session = await wealthService.initiateMfc(pan, mobile);
      setDetailsFor(null);
      setMfcSession(session);
      return;
    }
    const res = await wealthService.initiateAa(connection.key, { mobile, pan });
    setDetailsFor(null);
    setAaSession({ title: `Connect ${connection.label}`, redirectUrl: res.redirect_url, handle: res.consent_handle });
    await loadSummary(); // picks up the new pending consent and starts polling
  };

  // MF Central has no lasting consent: a refresh is a new OTP + QR fetch.
  // AA reuses the ACTIVE consent; if it was revoked or expired, ask for a new one.
  const refreshConnection = async (connection) => {
    setActionError('');
    if (connection.key === 'MFC') {
      startConnect(connection);
      return;
    }
    setRefreshing(connection.key);
    try {
      const res = await wealthService.refreshAa(connection.key);
      if (res.status === 'RECONNECT') {
        setActionError(`${res.message} We'll ask for your consent again.`);
        startConnect(connection);
      } else if (res.status === 'FAILED') {
        setActionError(res.message);
      }
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not refresh right now. Please try again.'));
    } finally {
      setRefreshing(null);
      loadSummary(); // picks up the in-flight refresh and starts polling it
    }
  };

  const handleMfcComplete = useCallback(() => {
    setMfcSession(null);
    loadSummary();
  }, [loadSummary]);

  // Finvu says the consent journey ended: close now and refresh right away.
  const finishAa = useCallback(() => {
    const handle = aaSession?.handle;
    setAaSession(null);
    if (handle) wealthService.getAaStatus(handle).catch(() => {}).finally(loadSummary);
  }, [aaSession, loadSummary]);

  // User closed the window. The backend re-checks Finvu and only cancels if still unapproved,
  // so the row shows "Consent not completed" instead of an endless loader.
  const closeAa = useCallback(() => {
    const handle = aaSession?.handle;
    setAaSession(null);
    if (handle) wealthService.cancelAa(handle).catch(() => {}).finally(loadSummary);
  }, [aaSession, loadSummary]);

  if (openCategory) {
    return (
      <CardContent>
        <HoldingsList category={openCategory} onBack={() => { setOpenCategory(null); loadSummary(); }} />
      </CardContent>
    );
  }

  return (
    <Box>
      <CardContent>
        <Box
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            border: '1px solid', borderColor: 'grey.400', borderRadius: 5, px: 2.5, py: 1.25, mb: 3,
          }}
        >
          <Typography variant="caption" sx={{ letterSpacing: 2, color: 'text.secondary' }}>TOTAL WEALTH</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {summary ? formatINR(summary.total_value) : '—'}
          </Typography>
        </Box>

        <Typography variant="overline" sx={{ letterSpacing: 3, color: 'text.secondary' }}>
          View your wealth
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ my: 2 }} action={<Button color="inherit" size="small" onClick={loadSummary}>Retry</Button>}>
            {loadError}
          </Alert>
        )}
        {actionError && <Alert severity="error" sx={{ my: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
        {!summary && !loadError && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

        {summary && (
          <Box sx={{ mt: 1 }}>
            {summary.connections.map((conn) => (
              conn.status === 'CONNECTED'
                ? (
                  <ConnectedRows
                    key={conn.key}
                    connection={conn}
                    onOpen={setOpenCategory}
                    refreshing={refreshing === conn.key}
                    onRefresh={() => refreshConnection(conn)}
                  />
                )
                : (
                  <ConnectRow
                    key={conn.key}
                    connection={conn}
                    stalled={Boolean(conn.pending_handle && stalled[conn.pending_handle])}
                    onCheckAgain={() => checkAgain(conn.pending_handle)}
                    onConnect={() => startConnect(conn)}
                  />
                )
            ))}
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button size="small" color="inherit" onClick={switchToManual}>
            Prefer to enter your investments manually?
          </Button>
        </Box>
      </CardContent>

      <ConnectDetailsDialog
        open={Boolean(detailsFor)}
        title={detailsFor ? `Connect ${detailsFor.label}` : ''}
        description={detailsFor?.key === 'MFC'
          ? "We'll fetch your mutual fund holdings from MF Central. They'll send an OTP to the mobile number registered with your folios."
          : "We'll request your consent through Finvu, an RBI-licensed Account Aggregator. Use the mobile number linked to your accounts."}
        defaultMobile={state.phoneNumber}
        onClose={() => setDetailsFor(null)}
        onSubmit={submitDetails}
      />
      {mfcSession && (
        <MfcCasModal session={mfcSession} onClose={() => setMfcSession(null)} onComplete={handleMfcComplete} />
      )}
      {aaSession && (
        <AaConsentModal title={aaSession.title} redirectUrl={aaSession.redirectUrl} onClose={closeAa} onFinished={finishAa} />
      )}
    </Box>
  );
}

export default WealthConnect;
